import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, ExternalLink } from 'lucide-react';

/**
 * Individual proposal card for list display
 * Follows Upwork's clean row design pattern
 */
const ProposalCard = ({
  proposal,
  onView,
  onArchive,
  showActions = true,
  index = -1,
  allProposals = []
}) => {
  const navigate = useNavigate();

  // Format date to relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears > 0) return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    if (diffMonths > 0) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    if (diffWeeks > 0) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return 'today';
  };

  // Format absolute date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
      ready_for_review: { color: 'bg-yellow-100 text-yellow-700', label: 'Ready for Review' },
      under_review: { color: 'bg-primary-100 text-primary-700', label: 'Under Review' },
      approved: { color: 'bg-success-100 text-success-700', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
      submitted: { color: 'bg-purple-100 text-purple-700', label: 'Submitted' },
      changes_requested: { color: 'bg-orange-100 text-orange-700', label: 'Changes Requested' },
      submission_failed: { color: 'bg-red-100 text-red-700', label: 'Submission Failed' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Get source badge
  const getSourceBadge = (source) => {
    if (source === 'upwork') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
          Upwork
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
        App
      </span>
    );
  };

  // Get job title
  const jobTitle = proposal.jobPostingId?.title || proposal.title || 'Untitled Job';
  const isUpworkProposal = proposal.source === 'upwork';
  const activityDate = isUpworkProposal
    ? (proposal.proposalSentAt || proposal.submittedAt || proposal.createdAt)
    : (proposal.submittedAt || proposal.createdAt);
  const activityLabel = (proposal.status === 'submitted' || isUpworkProposal) ? 'Submitted' : 'Initiated';

  const handleCardClick = () => {
    if (onView) {
      onView(proposal, index, allProposals);
    } else {
      navigate(`/proposals/${proposal._id}`);
    }
  };

  return (
    <div
      className="group px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-between gap-4">
        {/* Left: Date + Title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 flex-shrink-0">
              {activityLabel} {formatDate(activityDate)}
            </span>
            <h4 className="text-sm font-medium text-primary-700 hover:underline truncate">
              {jobTitle}
            </h4>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-400">{getRelativeTime(activityDate)}</span>
            {isUpworkProposal && (
              <span className="text-xs text-gray-400">General Profile</span>
            )}
          </div>
        </div>

        {/* Right: Badges + Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {getStatusBadge(proposal.status)}
          {getSourceBadge(proposal.source)}

          {/* Hover actions */}
          {showActions && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onArchive && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(proposal);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Archive"
                >
                  <Archive className="w-3.5 h-3.5" />
                </button>
              )}

              {isUpworkProposal && proposal.upworkUrl && (
                <a
                  href={proposal.upworkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="View on Upwork"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalCard;
